import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle, Eye, EyeOff, Loader2, ExternalLink, Trash2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

/**
 * AsaasConfig — Componente de configuração da integração Asaas da empresa.
 * Pode ser embutido em qualquer página (Perfil, Configurações, etc.)
 */
export default function AsaasConfig() {
  const [status, setStatus] = useState(null); // null | { connected, message }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/asaas-status');
      setStatus(data);
    } catch {
      setStatus({ connected: false, message: 'Erro ao verificar' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      await api.put('/settings/asaas-key', { api_key: apiKey });
      toast.success('API Key Asaas configurada com sucesso!');
      setApiKey('');
      setShowForm(false);
      await fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erro ao salvar API Key');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o Asaas? Você não poderá mais gerar cobranças automatizadas.')) return;
    try {
      await api.delete('/settings/asaas-key');
      toast.success('Integração Asaas removida');
      await fetchStatus();
    } catch {
      toast.error('Erro ao remover integração');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.get('/settings/asaas-status');
      if (data.connected) {
        toast.success(data.message || 'Conexão com Asaas OK! Chave válida.');
      } else {
        toast.error(data.message || 'Falha na conexão com Asaas. Verifique a chave.');
      }
    } catch {
      toast.error('Erro ao testar a conexão com o Asaas.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 md:p-6 transition-all duration-200">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-violet-100 text-violet-600 shadow-sm border border-violet-200/50">
          <Zap size={22} className={status?.connected ? "text-violet-600 fill-violet-200" : ""} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            Asaas — Cobranças
            {status?.connected && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase font-bold py-0.5 px-2">
                Conectado
              </Badge>
            )}
          </h3>
          <p className="text-sm text-slate-600 mt-0.5">
            Automatize o envio de cobranças aos seus clientes via PIX, Boleto ou Cartão pela plataforma.
          </p>
        </div>
      </div>

      {/* Loading Status */}
      {loading ? (
        <div className="flex items-center text-sm text-slate-500 py-4">
          <Loader2 size={16} className="animate-spin mr-2" /> Verificando status da conexão...
        </div>
      ) : (
        <div className="space-y-4">
          
          {/* Status Disconnected & No Form Active */}
          {!status?.connected && !showForm && (
            <div className="bg-white border text-sm text-slate-600 border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-slate-500">
                <XCircle size={18} className="text-slate-400" />
                <span>Integração não configurada no momento.</span>
              </div>
              <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700 text-white w-full sm:w-auto">
                Conectar Conta Asaas
              </Button>
            </div>
          )}

          {/* Connect Form */}
          {(!status?.connected && showForm) && (
            <form onSubmit={handleSave} className="bg-white border flex flex-col text-sm border-slate-200 rounded-lg p-5 shadow-sm">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700 font-medium">Chave de API (Asaas)</Label>
                    <a
                      href="https://app.asaas.com/apiConfig"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-violet-600 hover:text-violet-800 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={12} /> Onde pegou minha chave?
                    </a>
                  </div>
                  <div className="relative">
                    <Input
                      type={showKey ? 'text' : 'password'}
                      className="pl-4 pr-10 bg-slate-50 border-slate-300 focus:border-violet-500 focus:ring-violet-500 font-mono text-sm"
                      placeholder="$aact_..."
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      autoComplete="off"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                      onClick={() => setShowKey(!showKey)}
                    >
                      {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                    <Shield size={12} className="text-emerald-600" /> Sua chave é criptografada em nosso banco de dados. Nunca exibimos ela novamente.
                  </p>
                </div>
                
                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setApiKey(''); }} className="w-full sm:w-auto">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving || !apiKey.trim()} className="bg-violet-600 hover:bg-violet-700 text-white w-full sm:w-auto">
                    {saving ? <><Loader2 size={16} className="animate-spin mr-2" /> Validando...</> : 'Conectar Integração'}
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* Connected Info Panel */}
          {status?.connected && (
             <div className="space-y-4">
               {/* Connected state & Replace logic */}
               <div className="bg-white border border-emerald-100 rounded-lg p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                 <div>
                   <p className="text-sm font-medium text-slate-800 flex items-center gap-2 mb-1">
                     <CheckCircle size={16} className="text-emerald-500" /> Integração Ativa e Pronta
                   </p>
                   <p className="text-xs text-slate-500">
                     Sua estrutura de cobranças financeiras está conectada à sua conta de produção no Asaas.
                   </p>
                 </div>
                 <div className="flex items-center gap-2 w-full md:w-auto flex-col sm:flex-row">
                    <Button onClick={handleTestConnection} disabled={testing} variant="outline" className="w-full text-xs text-violet-700 border-violet-200 bg-violet-50 hover:bg-violet-100 hover:text-violet-800">
                      {testing ? <><Loader2 size={14} className="animate-spin mr-1" /> Testando...</> : 'Testar Vínculo'}
                    </Button>
                    <Button variant="outline" border="true" onClick={() => setShowForm(!showForm)} className="w-full text-xs">
                      {showForm ? 'Cancelar Troca de Chave' : 'Atualizar Chave'}
                    </Button>
                    <Button onClick={handleRemove} variant="ghost" className="w-full text-xs text-red-600 hover:bg-red-50 hover:text-red-700">
                      Desconectar
                    </Button>
                 </div>
               </div>

               {/* Replace Form */}
               {showForm && (
                  <form onSubmit={handleSave} className="bg-white/50 border border-violet-100 rounded-lg p-5 mt-4">
                    <div className="space-y-3">
                      <Label className="text-slate-700">Nova Chave de API</Label>
                      <div className="relative">
                        <Input
                          type={showKey ? 'text' : 'password'}
                          className="pl-4 pr-10 font-mono text-sm bg-white"
                          placeholder="$aact_..."
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          autoComplete="off"
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowKey(!showKey)}
                        >
                          {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button type="submit" disabled={saving || !apiKey.trim()} size="sm" className="bg-violet-600 hover:bg-violet-700">
                          {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Salvando...</> : 'Salvar Nova Chave'}
                        </Button>
                      </div>
                    </div>
                  </form>
               )}

               {/* Webhook Configuration Instructions */}
               <div className="bg-slate-800 rounded-lg p-5 shadow-sm text-slate-300">
                  <h4 className="flex items-center gap-2 font-medium text-white text-sm mb-3">
                    <ExternalLink size={16} className="text-violet-400" />
                    Configuração de Webhook (Importante!)
                  </h4>
                  <p className="text-xs mb-4 leading-relaxed">
                    Para que o nosso sistema atualize o status dos pagamentos automaticamente (Pago, Atrasado, etc.) no Financeiro, você precisa acessar o seu painel do Asaas e adicionar a URL de webhook abaixo:
                  </p>
                  
                  <div className="bg-black/50 border border-white/10 rounded-md p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <code className="text-xs text-violet-300 font-mono break-all w-full">
                      {window.location.origin.replace('3000', '8080')}/webhook/asaas/financial
                    </code>
                    <Button 
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin.replace('3000', '8080')}/webhook/asaas/financial`);
                        toast.success('URL copiada para área de transferência!');
                      }}
                      size="sm" 
                      variant="secondary" 
                      className="bg-white/10 hover:bg-white/20 text-white border-0 shrink-0 w-full sm:w-auto"
                    >
                      Copiar URL
                    </Button>
                  </div>
                  
                  <ul className="text-[11px] list-disc list-inside mt-4 space-y-1 opacity-80">
                    <li>Vá em Minha Conta {'>'} Integrações {'>'} Webhooks</li>
                    <li>Sincronize eventos de <strong>Cobranças</strong>.</li>
                  </ul>
               </div>

             </div>
          )}
        </div>
      )}
    </div>
  );
}
